import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';

export interface Recomendacion {
  id: number;
  cliente_id: number;
  tipo: 'combinacion' | 'alerta' | 'sugerencia';
  titulo: string;
  descripcion: string;
  leida: boolean;
  fecha: string;
}

export function useClienteRecomendaciones(initialData: Recomendacion[], clienteId?: number) {
  const storageKey = clienteId !== undefined
    ? `recomendaciones-leidas-${clienteId}`
    : 'recomendaciones-leidas';

  const readLocalReadIds = useCallback((): number[] => {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }, [storageKey]);

  const saveLocalReadId = useCallback((id: number) => {
    try {
      const ids = readLocalReadIds();
      if (!ids.includes(id)) {
        localStorage.setItem(storageKey, JSON.stringify([...ids, id]));
      }
    } catch {
      // ignore localStorage errors
    }
  }, [storageKey, readLocalReadIds]);

  const applyLocalOverrides = useCallback((rows: Recomendacion[]): Recomendacion[] => {
    const localIds = readLocalReadIds();
    if (localIds.length === 0) return rows;
    return rows.map((item) =>
      localIds.includes(item.id) ? { ...item, leida: true } : item,
    );
  }, [readLocalReadIds]);

  const [data, setData] = useState<Recomendacion[]>(applyLocalOverrides(initialData));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastClienteIdRef = useRef<number | undefined>(undefined);
  const isFirstMount = useRef(true);

  // On first mount or when clienteId changes: silently refetch in background
  // Keep existing data visible to avoid flash; update when response arrives
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      lastClienteIdRef.current = clienteId;
      setLoading(true);
      setData(applyLocalOverrides(initialData));
      return;
    }
    if (lastClienteIdRef.current !== clienteId) {
      lastClienteIdRef.current = clienteId;
      if (initialData.length > 0) {
        setData(applyLocalOverrides(initialData));
      }
      setError(null);
    }
  }, [initialData, clienteId, applyLocalOverrides]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let query = supabase
          .from('cliente_recomendaciones')
          .select('*')
          .order('fecha', { ascending: false });
        if (clienteId !== undefined) {
          query = query.eq('cliente_id', clienteId);
        }
        const { data: rows, error: err } = await query;
        if (err) {
          setError(err.message);
        } else if (rows && rows.length > 0) {
          setData(applyLocalOverrides(rows as Recomendacion[]));
        } else if (initialData.length > 0) {
          setData(applyLocalOverrides(initialData));
        }
      } catch {
        setError('Error de conexión. Usando datos locales.');
        if (initialData.length > 0) setData(applyLocalOverrides(initialData));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId]);

  const markAsRead = useCallback(async (id: number) => {
    // Always save locally first so it persists even if Supabase is empty
    saveLocalReadId(id);
    setData((prev) =>
      prev.map((item) => (item.id === id ? { ...item, leida: true } : item)),
    );

    try {
      const { error: err } = await supabase
        .from('cliente_recomendaciones')
        .update({ leida: true })
        .eq('id', id);
      if (err) {
        setError(err.message);
      }
    } catch {
      setError('Error al marcar como leída');
    }
  }, [saveLocalReadId]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from('cliente_recomendaciones')
        .select('*')
        .order('fecha', { ascending: false });
      if (clienteId !== undefined) {
        query = query.eq('cliente_id', clienteId);
      }
      const { data: rows, error: err } = await query;
      if (err) {
        setError(err.message);
      } else if (rows) {
        setData(applyLocalOverrides(rows as Recomendacion[]));
      }
    } catch {
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  }, [clienteId, applyLocalOverrides]);

  return { data, setData, markAsRead, refresh, loading, error };
}